// src/pages/marketplace/MarketplaceComparePage.tsx
// Side-by-side comparison of 2-3 products.

import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import { productApi } from '@core/api';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

/** Render filled/empty stars for a given rating (0-5). */
function StarRating({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`w-4 h-4 ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Format a number for display. */
function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/** Human-readable status. */
function statusLabel(status: string): string {
  return (status ?? '')
    .replace('PRODUCT_STATUS_', '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

interface CompareProduct {
  product: any;
  marketplace: any;
  dependencies: any[];
}

export const MarketplaceComparePage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const slugsParam = searchParams.get('products') || '';
  const slugs = slugsParam.split(',').filter(Boolean).slice(0, 3);

  const [items, setItems] = useState<CompareProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [addSearchOpen, setAddSearchOpen] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [addSearchResults, setAddSearchResults] = useState<any[]>([]);
  const [addSearchLoading, setAddSearchLoading] = useState(false);

  // Load data for each slug
  useEffect(() => {
    if (slugs.length === 0) {
      setItems([]);
      return;
    }

    setLoading(true);

    const loadAll = async () => {
      const results: CompareProduct[] = [];

      for (const s of slugs) {
        try {
          const prod = await store.GetProductBySlug(s);
          if (!prod) continue;

          let mk: any = null;
          let deps: any[] = [];

          try {
            const mkRes = await store.GetMarketplaceMetadata(prod.entity?.id ?? '');
            mk = mkRes?.marketplace ?? null;
          } catch { /* ignore */ }

          try {
            const depRes = await store.ListProductDependencies(prod.entity?.id ?? '');
            const depItems = depRes?.dependencies?.items;
            deps = Array.isArray(depItems) ? depItems : [];
          } catch { /* ignore */ }

          results.push({ product: prod, marketplace: mk, dependencies: deps });
        } catch { /* ignore */ }
      }

      setItems(results);
      setLoading(false);
    };

    loadAll();
  }, [slugsParam]);

  // Add product to comparison
  const addProduct = useCallback(
    (slug: string) => {
      if (slugs.length >= 3 || slugs.includes(slug)) return;
      const next = [...slugs, slug].join(',');
      setSearchParams({ products: next });
      setAddSearchOpen(false);
      setAddSearchQuery('');
      setAddSearchResults([]);
    },
    [slugs, setSearchParams],
  );

  // Remove product from comparison
  const removeProduct = useCallback(
    (slug: string) => {
      const next = slugs.filter((s) => s !== slug).join(',');
      if (next) {
        setSearchParams({ products: next });
      } else {
        setSearchParams({});
      }
    },
    [slugs, setSearchParams],
  );

  // Search for products to add
  const handleAddSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addSearchQuery.trim()) return;
    setAddSearchLoading(true);
    try {
      const res = await productApi.ListProducts({
        pagination: { cursor: '', limit: 10, direction: 'forward' },
        search: addSearchQuery.trim(),
      });
      const resultItems = res?.products?.items;
      setAddSearchResults(Array.isArray(resultItems) ? resultItems : []);
    } catch {
      setAddSearchResults(store.items ?? []);
    } finally {
      setAddSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex items-center justify-between">
        <h1 className="sr-only">{t('marketplace.compareProducts')}</h1>
        <Link to="/marketplace" className="ml-auto">
          <Button variant="secondary" size="sm">
            {t('marketplace.backToMarketplace')}
          </Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {items.length === 0 && !loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t('marketplace.noProductsToCompare')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('marketplace.addProductsHint')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Comparison table */}
      {items.length > 0 && (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">{t('marketplace.attribute')}</TableHead>
                  {items.map(({ product }) => (
                    <TableHead key={product.slug}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{product.name}</span>
                        <button
                          onClick={() => removeProduct(product.slug)}
                          className="text-xs text-muted-foreground hover:text-destructive"
                          title={t('marketplace.remove')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </TableHead>
                  ))}
                  {items.length < 3 && (
                    <TableHead className="w-48">
                      {/* Placeholder for add column */}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Icon + Name */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.product')}
                  </TableCell>
                  {items.map(({ product }) => (
                    <TableCell key={product.slug}>
                      <Link to={`/marketplace/${product.slug}`} className="flex items-center gap-3 group">
                        {product.icon_url ? (
                          <img src={product.icon_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                            style={{ backgroundColor: product.color_primary || '#6366f1' }}
                          >
                            {(product.name ?? '?')[0]?.toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm font-medium text-primary group-hover:underline">
                          {product.name}
                        </span>
                      </Link>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Tagline */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.tagline')}
                  </TableCell>
                  {items.map(({ product }) => (
                    <TableCell key={product.slug}>
                      <span className="text-sm text-muted-foreground">{product.tagline || '-'}</span>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Rating */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.rating')}
                  </TableCell>
                  {items.map(({ product, marketplace }) => (
                    <TableCell key={product.slug}>
                      <div className="flex items-center gap-2">
                        <StarRating rating={marketplace?.average_rating ?? 0} />
                        <span className="text-sm text-muted-foreground">
                          {(marketplace?.average_rating ?? 0).toFixed(1)}
                        </span>
                      </div>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Install count */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.installs')}
                  </TableCell>
                  {items.map(({ product, marketplace }) => (
                    <TableCell key={product.slug}>
                      <span className="text-sm">{formatCount(marketplace?.install_count ?? 0)}</span>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Status */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.status')}
                  </TableCell>
                  {items.map(({ product }) => (
                    <TableCell key={product.slug}>
                      <Badge variant="secondary" size="sm">{statusLabel(product.status)}</Badge>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Capabilities */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.capabilities')}
                  </TableCell>
                  {items.map(({ product }) => {
                    const caps = product.capabilities?.capabilities ?? [];
                    return (
                      <TableCell key={product.slug}>
                        <div className="flex flex-wrap gap-1">
                          {caps.length > 0 ? caps.map((cap: string) => {
                            const label = cap.replace('PRODUCT_CAPABILITY_', '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase());
                            return <Badge key={cap} variant="secondary" size="sm">{label}</Badge>;
                          }) : <span className="text-sm text-muted-foreground">-</span>}
                        </div>
                      </TableCell>
                    );
                  })}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Price/Plan */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.pricePlan')}
                  </TableCell>
                  {items.map(({ product }) => (
                    <TableCell key={product.slug}>
                      <span className="text-sm text-muted-foreground">
                        {product.is_builtin
                          ? t('marketplace.includedInPlatform')
                          : t('marketplace.contactForPricing')}
                      </span>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Dependencies count */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.dependencies')}
                  </TableCell>
                  {items.map(({ product, dependencies }) => (
                    <TableCell key={product.slug}>
                      <span className="text-sm">{dependencies.length}</span>
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Verified */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.verified')}
                  </TableCell>
                  {items.map(({ product, marketplace }) => (
                    <TableCell key={product.slug}>
                      {marketplace?.is_verified ? (
                        <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  ))}
                  {items.length < 3 && <TableCell />}
                </TableRow>

                {/* Supported locales */}
                <TableRow>
                  <TableCell className="font-medium text-muted-foreground">
                    {t('marketplace.locales')}
                  </TableCell>
                  {items.map(({ product, marketplace }) => {
                    const locales = marketplace?.supported_locales ?? [];
                    return (
                      <TableCell key={product.slug}>
                        <div className="flex flex-wrap gap-1">
                          {locales.length > 0
                            ? locales.map((loc: string) => (
                                <Badge key={loc} variant="secondary" size="sm">{loc}</Badge>
                              ))
                            : <span className="text-sm text-muted-foreground">-</span>}
                        </div>
                      </TableCell>
                    );
                  })}
                  {items.length < 3 && <TableCell />}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Add Product button */}
      {slugs.length < 3 && (
        <div>
          {!addSearchOpen ? (
            <Button
              variant="secondary"
              onClick={() => setAddSearchOpen(true)}
            >
              {t('marketplace.addProduct')}
            </Button>
          ) : (
            <Card>
              <CardContent className="p-4">
                <form onSubmit={handleAddSearch} className="flex gap-3 mb-4">
                  <Input
                    placeholder={t('marketplace.searchProductToAdd')}
                    value={addSearchQuery}
                    onChange={(e) => setAddSearchQuery(e.target.value)}
                    fullWidth={false}
                    className="flex-1"
                    autoFocus
                  />
                  <Button type="submit" variant="secondary" size="sm">
                    {t('marketplace.searchButton')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAddSearchOpen(false);
                      setAddSearchQuery('');
                      setAddSearchResults([]);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </form>

                {addSearchLoading && (
                  <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
                )}

                {addSearchResults.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {addSearchResults
                      .filter((p: any) => !slugs.includes(p.slug))
                      .map((p: any) => (
                        <button
                          key={p.entity?.id || p.slug}
                          onClick={() => addProduct(p.slug)}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          {p.icon_url ? (
                            <img src={p.icon_url} alt={p.name} className="w-8 h-8 rounded-md object-cover" />
                          ) : (
                            <div
                              className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: p.color_primary || '#6366f1' }}
                            >
                              {(p.name ?? '?')[0]?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <span className="text-sm font-medium text-foreground">{p.name}</span>
                            {p.tagline && (
                              <span className="block text-xs text-muted-foreground">{p.tagline}</span>
                            )}
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
});

export default MarketplaceComparePage;
